import { makeRequest } from './api-helpers';
import {
  PrestaShopParams,
  CategoryWS,
  ProductWS,
  ManufacturerWS,
  SpecificPricesByProductWS,
  TaxWS,
  TaxRuleWS,
  ProductFeatureValueWS,
  CustomerWS,
  AddressWS,
  StateWS,
  CreateCustomerPayload,
  UpdateCustomerPayload,
  CreateAddressPayload,
  UpdateAddressPayload,
  OrderWS,
  CarrierWS,
  CreateCartPayload,
  UpdateCartPayload,
  CartWS,
  CartRowWS,
  CreateOrderPayload,
  OrderPaymentWS,
  UpdateOrderPaymentPayload,
  DeliveryPriceRangeWS,
  DeliveryWS,
  DeliveryWeightRangeWS,
  ConfigurationWS,
  CartRuleWS,
  CreateOrderCartRulePayload,
  OrderCartRuleWS,
  CustomerThreadWS,
  CreateCustomerThreadPayload,
  CreateCustomerMessagePayload,
  CustomerMessageWS,
  StockAvailableWS,
} from '@/types/prestashop';
import {
  PRESTASHOP_CONFIG,
  PRESTASHOP_LANGUAGE_IDS,
  getPrestashopLanguageId,
  getPrestashopCurrencyId,
} from '@/config/prestashop';

// Constantes de configuración desde variables de entorno
// La definición vive en '@/config/prestashop'; aquí se re-exporta por compatibilidad.
export { PRESTASHOP_CONFIG, PRESTASHOP_LANGUAGE_IDS, getPrestashopLanguageId, getPrestashopCurrencyId };

// Parámetros por defecto para las llamadas a la API
const DEFAULT_PARAMS = {
  ws_key: PRESTASHOP_CONFIG.WS_KEY,
  output_format: 'JSON',
} as const;

// [Mantener todos los tipos (CategoryWS, ProductWS, etc.) sin cambios]

export async function getCategory(params: PrestaShopParams = {}): Promise<CategoryWS[]> {
  try {
    const data = await makeRequest<{ categories: CategoryWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'categories',
      { method: 'GET' },
      { ...DEFAULT_PARAMS, ...params }
    );
    return data.categories;
  } catch (error) {
    console.error(`Error obteniendo categorías: ${JSON.stringify(params)}`, error);
    throw error;
  }
}

export async function getProduct(
  params: PrestaShopParams = {},
  withPrices: boolean = true
): Promise<ProductWS[]> {
  try {
    let extendedParams: PrestaShopParams = {
      ...DEFAULT_PARAMS,
      ...params,
    };

    if (withPrices) {
      extendedParams = {
        ...extendedParams,
        'price[price_final_inc][use_tax]': 1,
        'price[price_final_exc][use_tax]': 0,
        'price[price_original_inc][use_reduction]': 0,
        'price[price_original_inc][use_tax]': 1,
        'price[price_original_exc][use_reduction]': 0,
        'price[price_original_exc][use_tax]': 0,
        'price[discount_inc][only_reduction]': 1,
        'price[discount_inc][use_tax]': 1,
        'price[discount_exc][only_reduction]': 1,
        'price[discount_exc][use_tax]': 0,
      };
    }

    const data = await makeRequest<{ products: ProductWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'products',
      { method: 'GET' },
      extendedParams
    );
    return data.products;
  } catch (error) {
    console.error(`Error obteniendo productos: ${JSON.stringify(params)}`, error);
    throw error;
  }
}

export async function getManufacturer(params: PrestaShopParams = {}): Promise<ManufacturerWS[]> {
  try {
    const data = await makeRequest<{ manufacturers: ManufacturerWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'manufacturers',
      { method: 'GET' },
      { ...DEFAULT_PARAMS, ...params }
    );
    return data.manufacturers;
  } catch (error) {
    console.error('Error obteniendo fabricantes:', error);
    throw error;
  }
}

export async function getSpecificPricesByProduct(
  productId: number
): Promise<SpecificPricesByProductWS[]> {
  try {
    const data = await makeRequest<{ specific_prices: SpecificPricesByProductWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'specific_prices',
      { method: 'GET' },
      {
        ...DEFAULT_PARAMS,
        display: 'full',
        filter: { id_product: productId },
      }
    );
    return data.specific_prices || [];
  } catch (error) {
    console.error('Error obteniendo precios específicos:', error);
    throw error;
  }
}

export async function getTaxByTaxRuleGroup(
  taxRuleGroupId: number,
  idCountry: number,
  language: number
): Promise<TaxWS | null> {
  try {
    // Obtener regla de impuesto
    const taxRulesData = await makeRequest<{ tax_rules: TaxRuleWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'tax_rules',
      { method: 'GET' },
      {
        ...DEFAULT_PARAMS,
        display: 'full',
        filter: {
          id_tax_rules_group: taxRuleGroupId,
          id_country: idCountry,
        },
        language,
      }
    );

    const taxRule = taxRulesData.tax_rules[0];
    if (!taxRule) return null;

    // Obtener impuesto
    const taxData = await makeRequest<{ taxes: TaxWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'taxes',
      { method: 'GET' },
      {
        ...DEFAULT_PARAMS,
        display: 'full',
        filter: { id: taxRule.id_tax },
        language,
      }
    );

    return taxData.taxes?.[0] || null;
  } catch (error) {
    console.error('Error obteniendo impuestos:', error);
    throw error;
  }
}

export async function getProductFeaturesValueByProductFeatureId(
  productFeatureId: number,
  language: number
): Promise<ProductFeatureValueWS | null> {
  try {
    const data = await makeRequest<{ product_feature_values: ProductFeatureValueWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'product_feature_values',
      { method: 'GET' },
      {
        ...DEFAULT_PARAMS,
        display: 'full',
        filter: {
          id: productFeatureId,
          id_feature: PRESTASHOP_CONFIG.PRESENTATION_FEATURE_ID,
        },
        language,
      }
    );
    return data.product_feature_values?.[0] || null;
  } catch (error) {
    console.error('Error obteniendo características del producto:', error);
    throw error;
  }
}

export async function createCustomer(customer: CreateCustomerPayload): Promise<CustomerWS | null> {
  try {
    const xmlPayload = buildCustomerXML(customer, false);
    const data = await makeRequest<{ customers: CustomerWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'customers',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/xml' },
        body: xmlPayload,
      },
      { ...DEFAULT_PARAMS, display: 'full' }
    );
    return data.customers?.[0] || null;
  } catch (error) {
    console.error('Error creando cliente:', error);
    throw error;
  }
}

export async function getCustomerByEmail(email: string): Promise<CustomerWS | null> {
  try {
    const data = await makeRequest<{ customers: CustomerWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'customers',
      { method: 'GET' },
      {
        ...DEFAULT_PARAMS,
        filter: { email },
        display: 'full',
      }
    );
    return data.customers?.[0] || null;
  } catch (error) {
    console.error('Error obteniendo cliente:', error);
    throw error;
  }
}

export async function updateCustomer(customer: UpdateCustomerPayload): Promise<CustomerWS | null> {
  try {
    const xmlPayload = buildCustomerXML(customer, true);
    const data = await makeRequest<{ customers: CustomerWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'customers',
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/xml' },
        body: xmlPayload,
      },
      { ...DEFAULT_PARAMS, display: 'full' }
    );
    return data.customers?.[0] || null;
  } catch (error) {
    console.error('Error actualizando cliente:', error);
    throw error;
  }
}

export async function getAddressByCustomerId(customerId: number): Promise<AddressWS[]> {
  try {
    const data = await makeRequest<{ addresses: AddressWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'addresses',
      { method: 'GET' },
      {
        ...DEFAULT_PARAMS,
        filter: { id_customer: customerId },
        display: 'full',
      }
    );
    return data.addresses || [];
  } catch (error) {
    console.error('Error obteniendo direcciones:', error);
    throw error;
  }
}

export async function getStateByCountryId(countryId: number): Promise<StateWS[]> {
  try {
    const data = await makeRequest<{ states: StateWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'states',
      { method: 'GET' },
      {
        ...DEFAULT_PARAMS,
        filter: {
          id_country: countryId,
          active: '1',
        },
        display: 'full',
      }
    );
    return data.states || [];
  } catch (error) {
    console.error('Error obteniendo estados:', error);
    throw error;
  }
}

export async function createAddress(address: CreateAddressPayload): Promise<AddressWS | null> {
  try {
    const xmlPayload = buildXML('address', address);
    const data = await makeRequest<{ addresses: AddressWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'addresses',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/xml' },
        body: xmlPayload,
      },
      { ...DEFAULT_PARAMS, display: 'full' }
    );
    return data.addresses?.[0] || null;
  } catch (error) {
    console.error('Error creando dirección:', error);
    throw error;
  }
}

export async function updateAddress(address: UpdateAddressPayload): Promise<AddressWS | null> {
  try {
    const xmlPayload = buildXML('address', address);
    const data = await makeRequest<{ addresses: AddressWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'addresses',
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/xml' },
        body: xmlPayload,
      },
      { ...DEFAULT_PARAMS, display: 'full' }
    );
    return data.addresses?.[0] || null;
  } catch (error) {
    console.error('Error actualizando dirección:', error);
    throw error;
  }
}

// Funciones auxiliares para construir XML
export function buildXML<T extends Record<string, any>>(entityName: string, data: T): string {
  const xmlFields = Object.entries(data)
    .map(([key, value]) => `              <${key}><![CDATA[${String(value ?? '')}]]></${key}>`)
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
          <prestashop xmlns:xlink="http://www.w3.org/1999/xlink">
            <${entityName}>
${xmlFields}
            </${entityName}>
          </prestashop>`;
}

function buildCustomerXML(
  c: CreateCustomerPayload | UpdateCustomerPayload,
  isUpdate: boolean = false
): string {
  const baseData = {
    lastname: removeNumbers(c.lastname),
    firstname: removeNumbers(c.firstname),
    email: c.email,
    active: c.active ?? 1,
  };

  if (isUpdate) {
    const updateData = {
      id: (c as UpdateCustomerPayload).id,
      ...baseData,
    };
    return buildXML('customer', updateData);
  } else {
    const createData = {
      id_default_group: (c as CreateCustomerPayload).id_default_group ?? 3,
      passwd: 'H1p3r4l1a@2025',
      ...baseData,
    };
    return buildXML('customer', createData);
  }
}

function removeNumbers(str: string): string {
  return str.replace(/[0-9]/g, '');
}

function buildCartXML(cart: CreateCartPayload | UpdateCartPayload): string {
  // Construir campos básicos del carrito
  const basicFields = [];

  // Campos obligatorios para creación
  if ('id_customer' in cart && cart.id_customer !== undefined) {
    basicFields.push(`              <id_customer><![CDATA[${cart.id_customer}]]></id_customer>`);
  }
  if ('id_address_delivery' in cart && cart.id_address_delivery !== undefined) {
    basicFields.push(
      `              <id_address_delivery><![CDATA[${cart.id_address_delivery}]]></id_address_delivery>`
    );
  }
  if ('id_address_invoice' in cart && cart.id_address_invoice !== undefined) {
    basicFields.push(
      `              <id_address_invoice><![CDATA[${cart.id_address_invoice}]]></id_address_invoice>`
    );
  }
  if ('id_currency' in cart && cart.id_currency !== undefined) {
    basicFields.push(
      `              <id_currency><![CDATA[${cart.id_currency || PRESTASHOP_CONFIG.CURRENCY_ID}]]></id_currency>`
    );
  }
  if ('id_lang' in cart && cart.id_lang !== undefined) {
    basicFields.push(
      `              <id_lang><![CDATA[${cart.id_lang || PRESTASHOP_CONFIG.LANGUAGE_ID}]]></id_lang>`
    );
  }

  // Campos opcionales para actualización
  if ('id' in cart && cart.id !== undefined) {
    basicFields.push(`              <id><![CDATA[${cart.id}]]></id>`);
  }
  if ('id_carrier' in cart && cart.id_carrier !== undefined) {
    basicFields.push(`              <id_carrier><![CDATA[${cart.id_carrier}]]></id_carrier>`);
  }

  if ('delivery_option' in cart && cart.delivery_option !== undefined) {
    basicFields.push(
      `              <delivery_option><![CDATA[${cart.delivery_option}]]></delivery_option>`
    );
  }

  const basicFieldsXML = basicFields.join('\n');

  // Construir asociaciones si existen
  let associationsXML = '';
  if (cart.associations && cart.associations.cart_rows && cart.associations.cart_rows.length > 0) {
    const cartRowsXML = cart.associations.cart_rows
      .map((row) =>
        [
          '                <cart_row>',
          `                  <id_product><![CDATA[${row.id_product}]]></id_product>`,
          `                  <id_product_attribute><![CDATA[${row.id_product_attribute}]]></id_product_attribute>`,
          `                  <id_address_delivery><![CDATA[${row.id_address_delivery}]]></id_address_delivery>`,
          `                  <id_customization><![CDATA[${row.id_customization}]]></id_customization>`,
          `                  <quantity><![CDATA[${row.quantity}]]></quantity>`,
          '                </cart_row>',
        ].join('\n')
      )
      .join('\n');

    associationsXML = [
      '              <associations>',
      '                <cart_rows>',
      cartRowsXML,
      '                </cart_rows>',
      '              </associations>',
    ].join('\n');
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
          <prestashop xmlns:xlink="http://www.w3.org/1999/xlink">
            <cart>
${basicFieldsXML}
${associationsXML}
            </cart>
          </prestashop>`;
}

export async function getOrderByCustomerId(customerId: number): Promise<OrderWS[]> {
  try {
    const data = await makeRequest<{ orders: OrderWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'orders',
      { method: 'GET' },
      { ...DEFAULT_PARAMS, filter: { id_customer: customerId }, display: 'full' }
    );
    return data.orders || [];
  } catch (error) {
    console.error('Error obteniendo pedidos:', error);
    throw error;
  }
}

export async function getOrderById(orderId: number): Promise<OrderWS | null> {
  try {
    const data = await makeRequest<{ orders: OrderWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      `orders`,
      { method: 'GET' },
      { ...DEFAULT_PARAMS, filter: { id: orderId }, display: 'full' }
    );
    return data.orders[0] || null;
  } catch (error) {
    console.error('Error obteniendo orden por ID:', error);
    throw error;
  }
}

export async function getOrderProducts(orderId: number): Promise<any[]> {
  try {
    const data = await makeRequest<{ order_rows: any[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      `orders/${orderId}`,
      { method: 'GET' },
      { ...DEFAULT_PARAMS, display: 'full' }
    );
    return data.order_rows || [];
  } catch (error) {
    console.error('Error obteniendo productos de la orden:', error);
    throw error;
  }
}

export async function getOrderByCartId(cartId: number): Promise<OrderWS | null> {
  try {
    const data = await makeRequest<{ orders: OrderWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'orders',
      { method: 'GET' },
      { ...DEFAULT_PARAMS, filter: { id_cart: cartId }, display: 'full' }
    );
    return data.orders?.[0] || null;
  } catch (error) {
    console.error('Error obteniendo orden por cart ID:', error);
    throw error;
  }
}

export async function getCarriers(
  lang: number = PRESTASHOP_CONFIG.LANGUAGE_ID
): Promise<CarrierWS[]> {
  try {
    const data = await makeRequest<{ carriers: CarrierWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'carriers',
      { method: 'GET' },
      { ...DEFAULT_PARAMS, display: 'full', filter: { active: '1', deleted: '0' }, language: lang }
    );
    return data.carriers || [];
  } catch (error) {
    console.error('Error obteniendo transportistas:', error);
    throw error;
  }
}

export async function getDeliveries(carrierId: number): Promise<DeliveryWS[]> {
  try {
    const data = await makeRequest<{ deliveries: DeliveryWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'deliveries',
      { method: 'GET' },
      { ...DEFAULT_PARAMS, display: 'full', filter: { id_carrier: carrierId } }
    );
    return data.deliveries || [];
  } catch (error) {
    console.error('Error obteniendo entregas:', error);
    throw error;
  }
}

export async function getDeliveryPriceRanges(carrierId: number): Promise<DeliveryPriceRangeWS[]> {
  try {
    const data = await makeRequest<{ price_ranges?: DeliveryPriceRangeWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'price_ranges',
      { method: 'GET' },
      { ...DEFAULT_PARAMS, display: 'full', filter: { id_carrier: carrierId } }
    );
    return data.price_ranges || [];
  } catch (error) {
    console.error('Error obteniendo entregas:', error);
    throw error;
  }
}

export async function getDeliveryWeightRanges(carrierId: number): Promise<DeliveryWeightRangeWS[]> {
  try {
    const data = await makeRequest<{ weight_ranges?: DeliveryWeightRangeWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'weight_ranges',
      { method: 'GET' },
      { ...DEFAULT_PARAMS, display: 'full', filter: { id_carrier: carrierId } }
    );
    return data.weight_ranges || [];
  } catch (error) {
    console.error('Error obteniendo entregas:', error);
    throw error;
  }
}

export async function createCart(cart: CreateCartPayload): Promise<CartWS | null> {
  try {
    const xmlPayload = buildCartXML(cart);
    const data = await makeRequest<{ carts: CartWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'carts',
      { method: 'POST', headers: { 'Content-Type': 'application/xml' }, body: xmlPayload },
      { ...DEFAULT_PARAMS, display: 'full' }
    );
    return data.carts?.[0] || null;
  } catch (error) {
    console.error('Error creando carrito:', error);
    throw error;
  }
}

export async function updateCart(cart: UpdateCartPayload): Promise<CartWS | null> {
  try {
    const xmlPayload = buildCartXML(cart);

    const data = await makeRequest<{ carts: CartWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'carts',
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/xml' },
        body: xmlPayload,
      },
      { ...DEFAULT_PARAMS, display: 'full' }
    );
    return data.carts?.[0] || null;
  } catch (error) {
    console.error('Error actualizando carrito:', error);
    throw error;
  }
}

export async function getCartById(cartId: number): Promise<CartWS | null> {
  try {
    const data = await makeRequest<{ carts: CartWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      `carts/${cartId}`,
      { method: 'GET' },
      { ...DEFAULT_PARAMS, display: 'full' }
    );
    return data.carts?.[0] || null;
  } catch (error) {
    console.error('Error obteniendo carrito:', error);
    throw error;
  }
}

export async function getCartsByCustomerId(customerId: number): Promise<CartWS[]> {
  try {
    const data = await makeRequest<{ carts: CartWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'carts',
      { method: 'GET' },
      {
        ...DEFAULT_PARAMS,
        display: 'full',
        filter: { id_customer: customerId },
      }
    );
    return data.carts || [];
  } catch (error) {
    console.error('Error obteniendo carritos del cliente:', error);
    throw error;
  }
}

export async function deleteCart(cartId: number): Promise<boolean> {
  try {
    await makeRequest(
      PRESTASHOP_CONFIG.BASE_URL,
      `carts/${cartId}`,
      { method: 'DELETE' },
      { ...DEFAULT_PARAMS }
    );
    return true;
  } catch (error) {
    console.error('Error eliminando carrito:', error);
    throw error;
  }
}

export async function addProductToCart(
  cartId: number,
  productId: number,
  quantity: number = 1,
  productAttributeId: number = 0,
  addressDeliveryId: number = 0
): Promise<CartWS | null> {
  try {
    // Obtener carrito actual
    const currentCart = await getCartById(cartId);
    if (!currentCart) {
      throw new Error('Carrito no encontrado');
    }

    // Crear nueva fila de carrito
    const newCartRow: CartRowWS = {
      id_product: productId.toString(),
      id_product_attribute: productAttributeId,
      id_address_delivery: addressDeliveryId,
      id_customization: 0,
      quantity: quantity,
    };

    // Agregar a las asociaciones existentes
    const updatedAssociations = {
      cart_rows: [...(currentCart.associations?.cart_rows || []), newCartRow],
    };

    // Actualizar carrito
    return await updateCart({
      id: cartId,
      associations: updatedAssociations,
    });
  } catch (error) {
    console.error('Error agregando producto al carrito:', error);
    throw error;
  }
}

export async function updateProductQuantityInCart(
  cartId: number,
  productId: number,
  quantity: number,
  productAttributeId: number = 0
): Promise<CartWS | null> {
  try {
    // Obtener carrito actual
    const currentCart = await getCartById(cartId);
    if (!currentCart) {
      throw new Error('Carrito no encontrado');
    }

    const currentCartRows = currentCart.associations?.cart_rows || [];

    // Buscar si el producto ya existe en el carrito
    const existingProductIndex = currentCartRows.findIndex(
      (row) =>
        parseInt(row.id_product) === productId && row.id_product_attribute === productAttributeId
    );

    let updatedCartRows: CartRowWS[];

    if (existingProductIndex !== -1) {
      // Producto encontrado - actualizar cantidad
      updatedCartRows = currentCartRows.map((row, index) => {
        if (index === existingProductIndex) {
          return { ...row, quantity: quantity };
        }
        return row;
      });
    } else {
      // Producto no encontrado - agregar como nueva fila
      const newCartRow: CartRowWS = {
        id_product: productId.toString(),
        id_product_attribute: productAttributeId,
        id_address_delivery: 0,
        id_customization: 0,
        quantity: quantity,
      };

      updatedCartRows = [...currentCartRows, newCartRow];
    }

    // Actualizar carrito
    return await updateCart({
      id: cartId,
      associations: { cart_rows: updatedCartRows },
    });
  } catch (error) {
    console.error('Error actualizando cantidad en carrito:', error);
    throw error;
  }
}

export async function removeProductFromCart(
  cartId: number,
  productId: number,
  productAttributeId: number = 0
): Promise<CartWS | null> {
  try {
    // Obtener carrito actual
    const currentCart = await getCartById(cartId);
    if (!currentCart) {
      throw new Error('Carrito no encontrado');
    }

    // Filtrar el producto a eliminar
    const updatedCartRows = (currentCart.associations?.cart_rows || []).filter((row) => {
      return !(
        parseInt(row.id_product) === productId && row.id_product_attribute === productAttributeId
      );
    });

    // Actualizar carrito
    return await updateCart({
      id: cartId,
      associations: { cart_rows: updatedCartRows },
    });
  } catch (error) {
    console.error('Error eliminando producto del carrito:', error);
    throw error;
  }
}

export async function createOrder(order: CreateOrderPayload): Promise<OrderWS | null> {
  try {
    const xmlPayload = buildOrderXML(order);
    const data = await makeRequest<{ orders: OrderWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'orders',
      { method: 'POST', headers: { 'Content-Type': 'application/xml' }, body: xmlPayload },
      { ...DEFAULT_PARAMS, display: 'full' }
    );
    const orderData = data.orders?.[0] || null;
    return orderData;
  } catch (error) {
    console.error('Error creando orden:', error);
    throw error;
  }
}

export function buildOrderXML(order: CreateOrderPayload): string {
  const basicFields = [
    `              <id_cart><![CDATA[${order.id_cart}]]></id_cart>`,
    `              <id_address_delivery><![CDATA[${order.id_address_delivery}]]></id_address_delivery>`,
    `              <id_address_invoice><![CDATA[${order.id_address_invoice}]]></id_address_invoice>`,
    `              <id_currency><![CDATA[${order.id_currency || PRESTASHOP_CONFIG.CURRENCY_ID}]]></id_currency>`,
    `              <id_lang><![CDATA[${order.id_lang || PRESTASHOP_CONFIG.LANGUAGE_ID}]]></id_lang>`,
    `              <id_customer><![CDATA[${order.id_customer}]]></id_customer>`,
    `              <id_carrier><![CDATA[${order.id_carrier}]]></id_carrier>`,
    `              <module><![CDATA[${order.module}]]></module>`,
    `              <payment><![CDATA[${order.payment}]]></payment>`,
    `              <total_paid><![CDATA[${order.total_paid}]]></total_paid>`,
    `              <total_paid_real><![CDATA[${order.total_paid_real}]]></total_paid_real>`,
    `              <total_products><![CDATA[${order.total_products}]]></total_products>`,
    `              <total_products_wt><![CDATA[${order.total_products_wt}]]></total_products_wt>`,
    `              <total_shipping><![CDATA[${order.total_shipping}]]></total_shipping>`,
    `              <total_discounts><![CDATA[${order.total_discounts}]]></total_discounts>`,
    `              <conversion_rate><![CDATA[${order.conversion_rate}]]></conversion_rate>`,
  ];

  return `<?xml version="1.0" encoding="UTF-8"?>
          <prestashop xmlns:xlink="http://www.w3.org/1999/xlink">
            <order>
            ${basicFields.join('\n')}
            </order>
          </prestashop>`;
}

export async function updateOrderState(orderId: number, stateId: number): Promise<OrderWS | null> {
  try {
    const xmlPayload = buildOrderStateXML(orderId, stateId);
    const data = await makeRequest<{ orders: OrderWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'orders',
      { method: 'PATCH', headers: { 'Content-Type': 'application/xml' }, body: xmlPayload },
      { ...DEFAULT_PARAMS, display: 'full' }
    );
    return data.orders?.[0] || null;
  } catch (error) {
    console.error('Error actualizando estado de la orden:', error);
    throw error;
  }
}

export function buildOrderStateXML(orderId: number, stateId: number): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
          <prestashop xmlns:xlink="http://www.w3.org/1999/xlink">
            <order>
              <id>${orderId}</id>
              <current_state>${stateId}</current_state>
            </order>
          </prestashop>`;
}

/**
 * Obtiene los pagos de una orden por referencia
 * @param orderReference - Referencia de la orden
 * @returns Promise<OrderPaymentWS[]>
 */
export async function getOrderPaymentsByReference(
  orderReference: string
): Promise<OrderPaymentWS[]> {
  try {
    const data = await makeRequest<{ order_payments: OrderPaymentWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'order_payments',
      { method: 'GET' },
      {
        ...DEFAULT_PARAMS,
        display: 'full',
        filter: { order_reference: orderReference },
      }
    );
    return data.order_payments || [];
  } catch (error) {
    console.error('Error obteniendo pagos de la orden:', error);
    throw error;
  }
}

/**
 * Actualiza un pago de orden
 * @param paymentData - Datos del pago a actualizar
 * @returns Promise<OrderPaymentWS | null>
 */
export async function updateOrderPayment(
  paymentData: UpdateOrderPaymentPayload
): Promise<OrderPaymentWS | null> {
  try {
    const xmlPayload = buildOrderPaymentXML(paymentData);
    const data = await makeRequest<{ order_payments: OrderPaymentWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'order_payments',
      { method: 'PATCH', headers: { 'Content-Type': 'application/xml' }, body: xmlPayload },
      { ...DEFAULT_PARAMS, display: 'full' }
    );
    return data.order_payments?.[0] || null;
  } catch (error) {
    console.error('Error actualizando pago de la orden:', error);
    throw error;
  }
}

/**
 * Construye el XML para actualizar un pago de orden
 * @param paymentData - Datos del pago
 * @returns string
 */
export function buildOrderPaymentXML(paymentData: UpdateOrderPaymentPayload): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
          <prestashop xmlns:xlink="http://www.w3.org/1999/xlink">
            <order_payments>
              <id><![CDATA[${paymentData.id}]]></id>
              <order_reference><![CDATA[${paymentData.order_reference}]]></order_reference>
              <transaction_id><![CDATA[${paymentData.transaction_id}]]></transaction_id>
              <amount><![CDATA[${paymentData.amount}]]></amount>
              <payment_method><![CDATA[${paymentData.payment_method}]]></payment_method>
              <conversion_rate><![CDATA[${paymentData.conversion_rate}]]></conversion_rate>
              <card_number><![CDATA[${paymentData.card_number}]]></card_number>
              <card_brand><![CDATA[${paymentData.card_brand}]]></card_brand>
              <card_expiration><![CDATA[${paymentData.card_expiration}]]></card_expiration>
              <card_holder><![CDATA[${paymentData.card_holder}]]></card_holder>
            </order_payments>
          </prestashop>`;
}

export function buildOrderCartRuleXML(orderCartRule: CreateOrderCartRulePayload): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
          <prestashop xmlns:xlink="http://www.w3.org/1999/xlink">
            <order_cart_rule>
              <id_order><![CDATA[${orderCartRule.id_order}]]></id_order>
              <id_cart_rule><![CDATA[${orderCartRule.id_cart_rule}]]></id_cart_rule>
              <name><![CDATA[${orderCartRule.name}]]></name>
              <value><![CDATA[${orderCartRule.value}]]></value>
              <value_tax_excl><![CDATA[${orderCartRule.value_tax_excl}]]></value_tax_excl>
              <free_shipping><![CDATA[${orderCartRule.free_shipping}]]></free_shipping>
            </order_cart_rule>
          </prestashop>`;
}

/**
 * Obtiene configuraciones de PrestaShop
 * @param params - Parámetros de búsqueda
 * @returns Promise<ConfigurationWS[]>
 */
export async function getConfigurations(params: PrestaShopParams = {}): Promise<ConfigurationWS[]> {
  try {
    const data = await makeRequest<{ configurations: ConfigurationWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'configurations',
      { method: 'GET' },
      { ...DEFAULT_PARAMS, ...params }
    );
    return data.configurations || [];
  } catch (error) {
    console.error('Error obteniendo configuraciones:', error);
    throw error;
  }
}

/**
 * Obtiene una configuración específica por nombre
 * @param configName - Nombre de la configuración
 * @param language - ID del idioma (opcional)
 * @returns Promise<ConfigurationWS | null>
 */
export async function getConfigurationByName(
  configName: string,
  language: number = PRESTASHOP_CONFIG.LANGUAGE_ID
): Promise<ConfigurationWS | null> {
  try {
    const data = await makeRequest<{ configurations: ConfigurationWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'configurations',
      { method: 'GET' },
      {
        ...DEFAULT_PARAMS,
        display: 'full',
        language,
        filter: { name: configName },
      }
    );
    return data.configurations?.[0] || null;
  } catch (error) {
    console.error('Error obteniendo configuración por nombre:', error);
    throw error;
  }
}

/**
 * Obtiene reglas de carrito
 * @param code - Código de la regla de carrito
 * @returns Promise<CartRuleWS[]>
 */

export async function getCartRulesByCode(
  code: string,
  language: number = PRESTASHOP_CONFIG.LANGUAGE_ID
): Promise<CartRuleWS | null> {
  try {
    const data = await makeRequest<{ cart_rules?: CartRuleWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'cart_rules',
      { method: 'GET' },
      { ...DEFAULT_PARAMS, display: 'full', language, filter: { code } }
    );
    return data.cart_rules?.[0] || null;
  } catch (error) {
    console.error('Error obteniendo reglas de carrito:', error);
    throw error;
  }
}

/**
 * Crea una regla de carrito para una orden
 * @param orderCartRule - Datos de la regla de carrito de la orden
 * @returns Promise<OrderCartRuleWS | null>
 */
export async function createOrderCartRule(
  orderCartRule: CreateOrderCartRulePayload
): Promise<OrderCartRuleWS | null> {
  try {
    const xmlPayload = buildOrderCartRuleXML(orderCartRule);
    const data = await makeRequest<{ order_cart_rules: OrderCartRuleWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'order_cart_rules',
      { method: 'POST', headers: { 'Content-Type': 'application/xml' }, body: xmlPayload },
      { ...DEFAULT_PARAMS, display: 'full' }
    );
    return data.order_cart_rules?.[0] || null;
  } catch (error) {
    console.error('Error creando regla de carrito de orden:', error);
    throw error;
  }
}

export async function createCustomerThread(
  customerThread: CreateCustomerThreadPayload
): Promise<CustomerThreadWS | null> {
  try {
    const xmlPayload = buildXML<CreateCustomerThreadPayload>('customer_thread', customerThread);
    const data = await makeRequest<{ customer_threads: CustomerThreadWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'customer_threads',
      { method: 'POST', headers: { 'Content-Type': 'application/xml' }, body: xmlPayload },
      { ...DEFAULT_PARAMS, display: 'full' }
    );
    return data.customer_threads?.[0] || null;
  } catch (error) {
    console.error('Error creando hilo de cliente:', error);
    throw error;
  }
}

export function buildCustomerThreadXML(customerThread: CreateCustomerThreadPayload): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
          <prestashop xmlns:xlink="http://www.w3.org/1999/xlink">
            <customer_thread>
              <id_lang><![CDATA[${customerThread.id_lang || PRESTASHOP_CONFIG.LANGUAGE_ID}]]></id_lang>
              <id_shop><![CDATA[${customerThread.id_shop}]]></id_shop>
              <id_customer><![CDATA[${customerThread.id_customer}]]></id_customer>
              <id_order><![CDATA[${customerThread.id_order}]]></id_order>
              <id_contact><![CDATA[${customerThread.id_contact}]]></id_contact>
              <email><![CDATA[${customerThread.email}]]></email>
              <token><![CDATA[${customerThread.token}]]></token>
              <status><![CDATA[${customerThread.status}]]></status>
            </customer_thread>
          </prestashop>`;
}

export async function getCustomerThreadsByCustomerId(
  customerId: number
): Promise<CustomerThreadWS[]> {
  try {
    const data = await makeRequest<{ customer_threads: CustomerThreadWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'customer_threads',
      { method: 'GET' },
      { ...DEFAULT_PARAMS, display: 'full', filter: { id_customer: customerId } }
    );

    const customerThreads = data.customer_threads || [];
    for (const customerThread of customerThreads) {
      const customerMessages = await getCustomerMessagesByCustomerThreadId(customerThread.id);
      customerThread.customer_messages = customerMessages;
    }

    return customerThreads;
  } catch (error) {
    console.error('Error obteniendo hilos de cliente:', error);
    throw error;
  }
}

export async function createCustomerMessage(
  customerMessage: CreateCustomerMessagePayload
): Promise<CustomerMessageWS | null> {
  try {
    const xmlPayload = buildCustomerMessageXML(customerMessage);
    const data = await makeRequest<{ customer_messages: CustomerMessageWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'customer_messages',
      { method: 'POST', headers: { 'Content-Type': 'application/xml' }, body: xmlPayload },
      { ...DEFAULT_PARAMS, display: 'full' }
    );
    return data.customer_messages?.[0] || null;
  } catch (error) {
    console.error('Error creando mensaje de cliente:', error);
    throw error;
  }
}

export function buildCustomerMessageXML(customerMessage: CreateCustomerMessagePayload): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
          <prestashop xmlns:xlink="http://www.w3.org/1999/xlink">
            <customer_message>
              <id_customer_thread><![CDATA[${customerMessage.id_customer_thread}]]></id_customer_thread>
              <message><![CDATA[${customerMessage.message}]]></message>
            </customer_message>
          </prestashop>`;
}

export async function getCustomerMessagesByCustomerThreadId(
  customerThreadId: number
): Promise<CustomerMessageWS[]> {
  try {
    const data = await makeRequest<{ customer_messages: CustomerMessageWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'customer_messages',
      { method: 'GET' },
      { ...DEFAULT_PARAMS, display: 'full', filter: { id_customer_thread: customerThreadId } }
    );
    return data.customer_messages || [];
  } catch (error) {
    console.error('Error obteniendo mensajes de cliente:', error);
    throw error;
  }
}

/**
 * Obtiene el stock disponible de un producto por su ID
 * @param productId - ID del producto
 * @returns Promise<StockAvailableWS[]>
 */
export async function getStockAvailableByProductId(productId: number): Promise<StockAvailableWS[]> {
  try {
    const data = await makeRequest<{ stock_availables: StockAvailableWS[] }>(
      PRESTASHOP_CONFIG.BASE_URL,
      'stock_availables',
      { method: 'GET' },
      {
        ...DEFAULT_PARAMS,
        display: 'full',
        filter: { id_product: productId },
      }
    );
    return data.stock_availables || [];
  } catch (error) {
    console.error(`Error obteniendo stock del producto ${productId}:`, error);
    throw error;
  }
}
